import { useState, useEffect, useCallback } from 'react';

import { observer } from 'mobx-react';

import type { WizardChildProps } from '../../../components/Wizard/types';
import { EmailVerificationLanding } from '../../../pages/Landing/DirectRegLanding/components/EmailVerificationLanding';
import DirectRegLanding from '../../../pages/Landing/DirectRegLanding/DirectRegLanding';
import ForgotPasswordLanding from '../../../pages/Landing/DirectRegLanding/ForgotPasswordLanding';
import ForgotPasswordSuccessLanding from '../../../pages/Landing/DirectRegLanding/ForgotPasswordSuccessLanding';
import { useStores } from '../../../stores/useStores';
import { LogIn } from '../LogIn/LogIn';
import { SignUpOptions } from '../SignUpOptions/SignUpOptions';
import { SignUpRegister } from '../SignUpRegister/SignUpRegister';
import { useOpenAuthWizard } from '../useOpenAuthWizard';

import { SignUpWizardProvider } from './signUpWizard.provider';

const stepsComponents = {
  DirectRegLanding,
  ForgotPasswordLanding,
  ForgotPasswordSuccessLanding,
  SignUpOptions,
  SignUpRegister,
  LogIn,
  EmailVerificationLanding,
};

const directRegLandingOrder: Array<keyof typeof stepsComponents> = ['DirectRegLanding', 'EmailVerificationLanding'];

const directEmailLandingOrder: Array<keyof typeof stepsComponents> = ['EmailVerificationLanding'];

const directSignUpLandingOrder: Array<keyof typeof stepsComponents> = [
  'SignUpOptions',
  'SignUpRegister',
  'EmailVerificationLanding',
];

const loginOrder: Array<keyof typeof stepsComponents> = ['LogIn', 'EmailVerificationLanding'];

const resetPasswordOrder: Array<keyof typeof stepsComponents> = [
  'ForgotPasswordLanding',
  'ForgotPasswordSuccessLanding',
];

const defaultOrder: Array<keyof typeof stepsComponents> = [
  'SignUpOptions',
  'SignUpRegister',
  'EmailVerificationLanding',
];

const getStepOrder = (
  authorized: boolean,
  flowType: 'signup' | 'login' | 'resetPassword' | undefined,
  isDirectEmailLanding: boolean,
  isDirectRegLanding: boolean,
  isDirectSignUpLanding: boolean,
) => {
  let stepsOrder = defaultOrder;

  if (flowType === 'login') {
    stepsOrder = loginOrder;
  } else if (flowType === 'resetPassword') {
    stepsOrder = resetPasswordOrder;
  } else if (!authorized && isDirectSignUpLanding) {
    stepsOrder = directSignUpLandingOrder;
  } else if (!authorized && isDirectEmailLanding) {
    stepsOrder = directEmailLandingOrder;
  } else if (!authorized && isDirectRegLanding) {
    stepsOrder = directRegLandingOrder;
  }

  return stepsOrder;
};

export default observer(function DirectRegWizard() {
  const { authStore } = useStores();

  const [currentFlowType, setCurrentFlowType] = useState<'signup' | 'login' | 'resetPassword' | undefined>('signup');
  const [cameFromResetPassword, setCameFromResetPassword] = useState(false);
  const [returnToMode, setReturnToMode] = useState<'signup' | 'login'>('signup');

  const stepOrder = getStepOrder(
    authStore.authorized,
    currentFlowType,
    authStore.isDirectEmailLanding(),
    authStore.isDirectRegLanding(),
    authStore.isDirectSignUpLanding(),
  );

  const [currentStep, setCurrentStep] = useState(0);

  const nextStep = useCallback(() => {
    setCurrentStep((prev) => Math.min(prev + 1, stepOrder.length - 1));
  }, [stepOrder.length]);

  const prevStep = useCallback(() => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  }, []);

  const forceStep = useCallback(
    (step: number, mode?: 'signup' | 'login') => {
      if (step === 1 && currentFlowType !== 'resetPassword') {
        setCurrentFlowType('resetPassword');
        setCurrentStep(0);
        return;
      }

      if (step === 0 && currentFlowType === 'resetPassword') {
        setCurrentFlowType('signup');
        setCameFromResetPassword(true);
        if (mode) {
          setReturnToMode(mode);
        }
        setCurrentStep(0);
        return;
      }

      setCurrentStep(Math.max(0, Math.min(step, stepOrder.length - 1)));
    },
    [currentFlowType, stepOrder.length],
  );

  useEffect(() => {
    setCurrentStep(0);
  }, [currentFlowType, stepOrder]);

  const { portalStore } = useStores();

  const { openAuthWizard } = useOpenAuthWizard();

  const onVerificationComplete = useCallback(() => {
    void authStore.handleVerificationCompletion();
    portalStore.closePortal();
    openAuthWizard('WelcomeBonusDialog', {
      persistent: true,
    });
  }, [authStore, portalStore, openAuthWizard]);

  useEffect(() => {
    const currentComponentName = stepOrder[currentStep];
    if (currentComponentName !== 'DirectRegLanding' && cameFromResetPassword) {
      setCameFromResetPassword(false);
    }
  }, [currentStep, stepOrder, cameFromResetPassword]);

  const CurrentComponent = stepsComponents[stepOrder[currentStep]];

  if (!CurrentComponent) {
    return null;
  }

  const componentProps: WizardChildProps & {
    initialMode?: 'signup' | 'login';
    onVerificationComplete?: () => void;
  } = {
    isCurrent: true,
    nextStep,
    prevStep,
    forceStep,
  };

  if (CurrentComponent === stepsComponents.DirectRegLanding && cameFromResetPassword) {
    componentProps.initialMode = returnToMode;
  }

  if (CurrentComponent === stepsComponents.EmailVerificationLanding) {
    componentProps.onVerificationComplete = onVerificationComplete;
  }

  return (
    <SignUpWizardProvider>
      <CurrentComponent {...(componentProps as WizardChildProps & { onVerificationComplete: () => void })} />
    </SignUpWizardProvider>
  );
});
